"use client";

import { useRef, useState, type ReactNode } from "react";
import { useTranslations } from "use-intl";

import { MoreIcon } from "@/components/appIcons";

interface DetailActionsProps {
  listening: ReactNode;
  listeningProvider: ReactNode;
  collection?: ReactNode;
  overflow: ReactNode;
  admin?: ReactNode;
}

export function DetailActions({
  listening,
  listeningProvider,
  collection,
  overflow,
  admin,
}: DetailActionsProps) {
  const t = useTranslations("shared.detailActions");
  const overflowTriggerRef = useRef<HTMLButtonElement>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      {/* Fills the hero column: Tenho/Quero have a fixed width on desktop, listening takes the rest. */}
      <div className="grid w-full grid-cols-[minmax(0,1fr)_2.75rem] items-stretch gap-2 md:flex">
        <div className="flex min-w-0 md:flex-1 [&>*]:flex-1">{listening}</div>
        <div className="shrink-0">{listeningProvider}</div>
        {collection && <div className="min-w-0 md:flex md:w-72 md:shrink-0">{collection}</div>}
        <div
          className="relative shrink-0"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setOverflowOpen(false);
              overflowTriggerRef.current?.focus();
            }
          }}
        >
          <button
            ref={overflowTriggerRef}
            type="button"
            aria-label={t("moreActions")}
            aria-expanded={overflowOpen}
            onClick={() => setOverflowOpen((open) => !open)}
            className="flex size-11 items-center justify-center rounded-lg border border-border bg-secondary text-muted-foreground transition-colors hover:bg-secondary/80 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <MoreIcon className="text-xl" decorative />
          </button>
          {/* Keep actions mounted so portaled dialogs preserve their state after the menu closes. */}
          <div
            hidden={!overflowOpen}
            className="absolute right-0 top-full z-40 mt-2 w-64 rounded-xl border border-border bg-popover p-2 text-popover-foreground shadow-xl"
            onClick={(event) => {
              if ((event.target as HTMLElement).closest("button, a")) setOverflowOpen(false);
            }}
          >
            <div className="flex flex-col gap-0.5">{overflow}</div>
          </div>
        </div>
      </div>
      {admin && <div className="flex min-h-11 items-stretch gap-2 empty:hidden">{admin}</div>}
    </div>
  );
}
